import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { useState, useEffect } from 'react';
import Landing from './pages/Landing';
import AuthCallback from './pages/AuthCallback';
import Login from './pages/Login';
import Dashboard from './pages/Dashboard';
import WhispersEnhanced from './pages/WhispersEnhanced';
import Beneficiaries from './pages/Beneficiaries';
import Benefactors from './pages/Benefactors';
import FamilyTree from './pages/FamilyTree';
import SharedFamilyTree from './pages/SharedFamilyTree';
import FamilyCrestStudio from './components/family-tree/FamilyCrestStudio';
import Albums from './pages/Albums';
import Profile from './pages/Profile';
import SavingsBeneficiaries from './pages/SavingsBeneficiaries';
import ExecutorRelease from './pages/ExecutorRelease';
import BeneficiaryAccess from './pages/BeneficiaryAccess';
import WillUpload from './pages/WillUpload';
import Belongings from './pages/Belongings';
import FuneralPreferences from './pages/FuneralPreferences';
import CredentialsVault from './pages/CredentialsVault';
import Obituary from './pages/Obituary';
import Feed from './pages/Feed';
import Insurance from './pages/Insurance';
import PaymentMethods from './pages/PaymentMethods';
import AcceptBeneficiary from './pages/AcceptBeneficiary';
import WhisperView from './pages/WhisperView';
import EasyPassingTrigger from './pages/EasyPassingTrigger';
import Inbox from './pages/Inbox';
import ServiceOverview from './pages/ServiceOverview';
import LeadCapture from './pages/LeadCapture';
import Privacy from './pages/Privacy';
import Terms from './pages/Terms';
import MilitaryRecords from './pages/MilitaryRecords';
import Recipes from './pages/Recipes';
import PetCare from './pages/PetCare';
import PropertyRecords from './pages/PropertyRecords';
import Membership from './pages/Membership';
import FamilyReadiness from './pages/FamilyReadiness';
import ProtectedRoute from './components/ProtectedRoute';
import PinGate from './components/PinGate';
import NovaAssistant from './components/NovaAssistant';
import AdminLogin from './pages/admin/AdminLogin';
import AdminApp from './pages/admin/AdminApp';
import AmbassadorApply from './pages/ambassador/AmbassadorApply';
import AmbassadorLogin from './pages/ambassador/AmbassadorLogin';
import AmbassadorDashboard from './pages/ambassador/AmbassadorDashboard';
import AmbassadorLanding from './pages/ambassador/AmbassadorLanding';
import AmbassadorResources from './pages/ambassador/AmbassadorResources';
import AmbassadorSettings from './pages/ambassador/AmbassadorSettings';
import AmbassadorTerms from './pages/ambassador/AmbassadorTerms';
import GroupLanding from './pages/GroupLanding';
import { useLocation } from 'react-router-dom';
import { Toaster } from './components/ui/sonner';
import './App.css';

// Capture partner referral code from the initial URL (?ref=CODE) so it survives navigation to signup.
try {
  const _ref = new URLSearchParams(window.location.search).get('ref');
  if (_ref) localStorage.setItem('referral_code', _ref.trim());
} catch (e) { /* ignore */ }

function App() {
  return (
    <div className="App">
      <BrowserRouter>
        <AppRouter />
      </BrowserRouter>
      <Toaster position="top-right" />
    </div>
  );
}

function AppRouter() {
  // Check for Google Auth session_id in URL hash
  const hash = window.location.hash;
  const hasSessionId = hash && hash.includes('session_id=');
  
  // If we have a session_id, render AuthCallback to process it
  if (hasSessionId) {
    return <AuthCallback />;
  }
  
  return (
    <>
    <Routes>
      <Route path="/" element={<Landing />} />
      <Route path="/about" element={<ServiceOverview />} />
      <Route path="/services" element={<ServiceOverview />} />
      <Route path="/how-it-works" element={<ServiceOverview />} />
      <Route path="/lead" element={<LeadCapture />} />
      <Route path="/get-started" element={<LeadCapture />} />
      <Route path="/login" element={<Login />} />
      <Route path="/privacy" element={<Privacy />} />
      <Route path="/terms" element={<Terms />} />
      <Route path="/auth-callback" element={<AuthCallback />} />
      <Route path="/accept-beneficiary" element={<AcceptBeneficiary />} />
      <Route path="/whisper/view" element={<WhisperView />} />
      <Route
        path="/dashboard"
        element={
          <ProtectedRoute>
            <Dashboard />
          </ProtectedRoute>
        }
      />
      <Route
        path="/whispers"
        element={
          <ProtectedRoute>
            <WhispersEnhanced />
          </ProtectedRoute>
        }
      />
      <Route
        path="/beneficiaries"
        element={
          <ProtectedRoute>
            <Beneficiaries />
          </ProtectedRoute>
        }
      />
      <Route
        path="/benefactors"
        element={
          <ProtectedRoute>
            <Benefactors />
          </ProtectedRoute>
        }
      />
      <Route
        path="/family-tree"
        element={
          <ProtectedRoute>
            <FamilyTree />
          </ProtectedRoute>
        }
      />
      <Route path="/family-tree/shared/:token" element={<SharedFamilyTree />} />
      <Route
        path="/family-tree/crest"
        element={
          <ProtectedRoute>
            <FamilyCrestStudio />
          </ProtectedRoute>
        }
      />
      <Route
        path="/albums"
        element={
          <ProtectedRoute>
            <Albums />
          </ProtectedRoute>
        }
      />
      <Route
        path="/savings"
        element={
          <ProtectedRoute>
            <PinGate title="Savings & Beneficiary Planning">
              <SavingsBeneficiaries />
            </PinGate>
          </ProtectedRoute>
        }
      />
      <Route
        path="/profile"
        element={
          <ProtectedRoute>
            <Profile />
          </ProtectedRoute>
        }
      />
      <Route
        path="/easy-passing/:userId"
        element={
          <ProtectedRoute>
            <EasyPassingTrigger />
          </ProtectedRoute>
        }
      />
      <Route
        path="/will"
        element={
          <ProtectedRoute>
            <PinGate title="Will & Legal Documents">
              <WillUpload />
            </PinGate>
          </ProtectedRoute>
        }
      />
      <Route
        path="/belongings"
        element={
          <ProtectedRoute>
            <Belongings />
          </ProtectedRoute>
        }
      />
      <Route
        path="/funeral"
        element={
          <ProtectedRoute>
            <FuneralPreferences />
          </ProtectedRoute>
        }
      />
      <Route
        path="/credentials"
        element={
          <ProtectedRoute>
            <PinGate title="Credentials Vault">
              <CredentialsVault />
            </PinGate>
          </ProtectedRoute>
        }
      />
      <Route
        path="/obituary"
        element={
          <ProtectedRoute>
            <Obituary />
          </ProtectedRoute>
        }
      />
      <Route
        path="/feed"
        element={
          <ProtectedRoute>
            <Feed />
          </ProtectedRoute>
        }
      />
      <Route
        path="/insurance"
        element={
          <ProtectedRoute>
            <Insurance />
          </ProtectedRoute>
        }
      />
      <Route
        path="/payment-methods"
        element={
          <ProtectedRoute>
            <PaymentMethods />
          </ProtectedRoute>
        }
      />
      <Route
        path="/inbox"
        element={
          <ProtectedRoute>
            <Inbox />
          </ProtectedRoute>
        }
      />
      <Route path="/military" element={<ProtectedRoute><PinGate title="Military Records"><MilitaryRecords /></PinGate></ProtectedRoute>} />
      <Route path="/recipes" element={<ProtectedRoute><Recipes /></ProtectedRoute>} />
      <Route path="/pet-care" element={<ProtectedRoute><PetCare /></ProtectedRoute>} />
      <Route path="/property" element={<ProtectedRoute><PinGate title="Property Records"><PropertyRecords /></PinGate></ProtectedRoute>} />
      <Route path="/membership" element={<ProtectedRoute><Membership /></ProtectedRoute>} />
      <Route path="/readiness" element={<ProtectedRoute><FamilyReadiness /></ProtectedRoute>} />
      <Route path="/admin/login" element={<AdminLogin />} />
      <Route path="/admin" element={<AdminApp />} />
      <Route path="/admin/*" element={<AdminApp />} />
      <Route path="/ambassador/apply" element={<AmbassadorApply />} />
      <Route path="/ambassador/terms" element={<AmbassadorTerms />} />
      <Route path="/group/:code" element={<GroupLanding />} />
      <Route path="/executor/release" element={<ExecutorRelease />} />
      <Route path="/beneficiary/access" element={<BeneficiaryAccess />} />
      <Route path="/ambassador/login" element={<AmbassadorLogin />} />
      <Route path="/ambassador/set-password" element={<AmbassadorLogin />} />
      <Route path="/ambassador/dashboard" element={<AmbassadorDashboard />} />
      <Route path="/ambassador/resources" element={<AmbassadorResources />} />
      <Route path="/ambassador/settings" element={<AmbassadorSettings />} />
      <Route path="/ambassador/:code" element={<AmbassadorLanding />} />
    </Routes>
    <NovaGate />
    </>
  );
}

function NovaGate() {
  const { pathname } = useLocation();
  if (pathname.startsWith('/admin') || pathname.startsWith('/ambassador')) return null;
  return <NovaAssistant />;
}

export default App;